<script>
   /*************************************************************/
   /* ModelLoader                                               */
   /* -------------------------                                 */
   /* loads NIMCA model from file                               */
   /*************************************************************/

   import { FileSelector, ButtonCancel, Container, Button } from 'svelte-controls-basic';
   import { isvector } from 'mdatools/arrays';
   import { reviver as baseReviver } from '@mdatools-apps/shared/utils';
   import { checkPrepField } from '@mdatools-apps/shared/methods/prep.js';
   import { NIMCA } from '../utils.svelte';

   import PrepInfo from '@mdatools-apps/shared/components/PrepInfo.svelte';

   let { prep = $bindable(), nimca = $bindable() } = $props();    // object with model


   // string with model file name
   let modelFile = $state('');

   // app status for handling and showing errors
   let status = $state('neutral');
   let errMsg = $state('');

   const isPosInt = v => Number.isInteger(v) && v > 0;

   // the file is user-supplied and every field below is read without a further check somewhere in
   // the app: a missing 'Nfc' makes 'qchisq' return NaN and every distance follows it, a short
   // 'mX' centres only part of the data, and a 'prep' without 'npred' passes the loader and then
   // throws from inside 'prep_apply'. so the raw JSON is checked before it becomes a model
   function checkModel(m) {

      const isPosNum = v => Number.isFinite(v) && v > 0;
      const isPerc   = v => Number.isFinite(v) && v > 0 && v < 100;

      if (!isPosInt(m.nrows) || !isPosInt(m.ncols))
         return 'the training set size is missing or is not a positive whole number';
      if (!isPosInt(m.Nfc) || !isPosInt(m.Nfr))
         return 'the degrees of freedom are missing or are not positive whole numbers';
      if (!isPosNum(m.f0c) || !isPosNum(m.f0r))
         return 'the distance scaling factors are missing or are not positive';
      if (!isvector(m.mX) || m.mX.length !== m.ncols || !m.mX.v.every(Number.isFinite))
         return 'the centering values do not match the ' + m.ncols + ' predictors of the model';
      if (!isvector(m.sX) || m.sX.length !== m.ncols || !m.sX.v.every(v => Number.isFinite(v) && v !== 0))
         return 'the scaling values do not match the ' + m.ncols + ' predictors of the model, or one of them is zero';
      if (!isPerc(m.alpha) || !isPerc(m.gamma))
         return 'the significance levels are missing or lie outside 0-100%';
      if (m.limType !== 'classic' && m.limType !== 'robust')
         return "the limits estimator is neither 'classic' nor 'robust'";
      if (typeof m.center !== 'boolean' || typeof m.scale !== 'boolean')
         return 'the centering and scaling flags are missing or are not true/false';

      return '';
   }

   // gets model content as one large string, parse it into matrix
   function setModel(strData) {
      try{
         // parsed with the base reviver, so the nested vectors are revived but the model itself
         // stays plain JSON: it is validated first and only then constructed. the app reviver
         // builds the NIMCA on the way in and its constructor writes 'class' whatever the file
         // said, which left the marker check below unable to reject anything
         const m = JSON.parse(strData, baseReviver);

         // every step here has to hold before the next one is allowed to look inside. reaching
         // 'm.nimca.class[0]' while 'm.nimca' was a number or missing raised a raw TypeError, and
         // the user was shown its text instead of a sentence about the file
         const isObject = v => v !== null && typeof v === 'object';

         if (!isObject(m) || !isObject(m.nimca) || !Object.hasOwn(m, 'prep') ||
             m.nimca.__type !== 'NIMCA' ||
             !Array.isArray(m.nimca.class) || m.nimca.class[0] !== 'nimcamodel') {
            throw Error('The JSON file you selected does not contain a NIMCA model.');
         }

         // alpha and gamma are numbers everywhere the app makes them, but a file written by an
         // older version could carry them as text. a numeric string becomes a number here, so the
         // check below and everything after it see one type - 'Select' compares its options with
         // 'Object.is', and a string would leave the settings control showing nothing selected
         for (const k of ['alpha', 'gamma']) {
            if (typeof m.nimca[k] === 'string' && m.nimca[k].trim() !== '') {
               m.nimca[k] = Number(m.nimca[k]);
            }
         }

         const err = checkModel(m.nimca);
         if (err !== '') {
            throw Error('The model file is damaged: ' + err + '.');
         }

         // the preprocessing is rendered by the info panel, where no catch reaches, and applied by
         // 'prep_apply': the checks of a preprocessing model, none for 0 or an empty object
         try { checkPrepField(m.prep); } catch {
            throw Error('The model file is damaged: its preprocessing can not be read.');
         }

         m.nimca['fileName'] = modelFileName;
         prep = m.prep;
         nimca = NIMCA.fromJSON(m.nimca);
         status = 'success';
         errMsg = ''
      } catch (e) {
         nimca = null;
         prep = null;
         status = 'error';
         // a file the parser refuses is named as such, in place of the parser's own text
         errMsg = e instanceof SyntaxError ? 'The file could not be read as JSON.'
            : e instanceof Error ? e.message : String(e);
      }
   }

   // promise for reading the JSON file
   function readFile () {
      const reader = new FileReader();
      reader.onload = () => { setModel(reader.result) };
      reader.readAsText(modelFile);
   }

   function reset() {
      modelFile = '';
      nimca = null;
      prep = null;
      status = 'neutral';
      errMsg = '';
   }

   // reactive expression for tracking changes of 'modelFile'
   $effect(() => {
      if (modelFile === null) {
         // if model file is empty reset status and error messages
         nimca = null;
         prep = null;
         status = 'neutral';
         errMsg = '';
      } else {
         // if not just remove error messages if any
         status = 'neutral';
         errMsg = '';
      }
   });

   // file name with model without extension
   let modelFileName =$derived(modelFile ? modelFile.name.replace(/\.[^/.]+$/, "") : '');
</script>

{#if !nimca }

   <!-- show if data is not loaded -->
   <Container>
      <FileSelector bind:file={modelFile} acceptType=".json" message="Select file with model"/>
   </Container>

   {#if modelFile}
   <Container label="&nbsp;">
      <Button onclick={readFile} text="Load" />
   </Container>
   {/if}
{:else}

   <!-- dataset is loaded - show info and cancel button -->
   <Container name="info">
      <p class="model-info">
         <span>Model file:</span> <span title={nimca.fileName} class="file-name">{nimca.fileName}</span><br>
         <span style="color:#a0a0a0;">Model:</span> {nimca.ncols} predictors{nimca.center ? ', centered' : ''}{nimca.scale ? ', scaled' : ''}
         {#if prep}
         <br><span style="color:#a0a0a0;">Preprocessing: </span>
            <PrepInfo prepmodel={prep} />
         {/if}
      </p>
      <ButtonCancel onclick={reset} />
   </Container>

{/if}

<!-- error message -->
{#if status === 'error' || errMsg}
<Container status="error">
   <p>{errMsg}</p>
</Container>
{/if}


<style>

   .model-info {
      line-height: 1.5em;
   }

   .model-info > :global(span) {
      vertical-align:top;
      color: #a0a0a0;
   }

   .model-info > .file-name {
      display: inline-block;
      padding: 0;
      margin: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 25ch;
      color: #222;
   }

</style>